import type { DatePickerPassThroughMethodOptions, DatePickerPassThroughOptions } from 'primevue';
import type { ComputedRef, Ref } from 'vue';
import { usePrimeVue } from 'primevue/config';
import {
  computed,

} from 'vue';

import type { IFormDateBaseInput } from '@modules/forms/types';
import { useDevice } from '@modules/core/composables/device';

export interface DateInputComposable {
  format: ComputedRef<string | undefined>;
  isMobile: Ref<boolean>;
  inputPtClass: string;
  adjustDateForViewMode(view: IFormDateBaseInput['view'], value: Date): Date;
  monthPtFunction(options: DatePickerPassThroughMethodOptions): DatePickerPassThroughOptions;
  yearPtFunction(options: DatePickerPassThroughMethodOptions): DatePickerPassThroughOptions;
}

export default function useDateInput(dateFormat: Ref<string | undefined>): DateInputComposable {
  const primevue = usePrimeVue();
  const { isMobile } = useDevice();

  const format = computed(() => dateFormat.value || primevue.config.locale?.dateFormat || 'yy-mm-dd');

  const inputPtClass = 'cursor-pointer w-full';

  function adjustDateForViewMode(view: IFormDateBaseInput['view'], value: Date): Date {
    const newValue = value;
    switch (view) {
      case 'year':
        newValue.setMonth(6);
        newValue.setDate(15);
        return newValue;
      case 'month':
        newValue.setDate(15);
        return newValue;
      default:
        return newValue;
    }
  }

  function monthPtFunction(options: DatePickerPassThroughMethodOptions): object {
    const date = new Date();
    const currentYear = date.getFullYear();
    const currentMonthIndex = date.getMonth();
    const attrs = {
      'data-test-el-attr-selected': options.context.selected,
      'data-test-el-attr-month-index': options.context.monthIndex,
    };

    if (options.state.currentYear === currentYear && options.context.monthIndex === currentMonthIndex) {
      return {
        ...attrs,
        'class': 'border border-inherit',
        'data-test-el': 'current-month',
      };
    }

    return {
      ...attrs,
      class: 'border border-transparent',
    };
  }

  function yearPtFunction(options: DatePickerPassThroughMethodOptions): object {
    const date = new Date();
    const currentYear = date.getFullYear();
    const attrs = {
      'data-test-el-attr-selected': options.context.selected,
      'data-test-el-attr-year': options.context.year.value,
    };

    if (options.context.year.value === currentYear) {
      return {
        ...attrs,
        'class': 'border border-inherit',
        'data-test-el': 'current-year',
      };
    }

    return {
      ...attrs,
      class: 'border border-transparent',
    };
  }

  return {
    format,
    isMobile,
    inputPtClass,
    adjustDateForViewMode,
    monthPtFunction,
    yearPtFunction,
  };
}
